import type { ReactNode } from 'react';
import { Fragment, useState } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { ProgressBar } from '@launchpad-ui/components';

import type { ActivityStep, ThoughtProcess } from './activityGrouping';
import { SpanDetailsIconButton } from './internal/SpanDetailsIconButton';
import { InvestigationRow } from './InvestigationRow';
import { ErrorChip } from './ToolCallRow';

import { bar, detailsLink, thoughtRow, usage } from './ThoughtProcessSection.css';

const formatDuration = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m ${totalSeconds % 60}s`;
  }
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const formatTokens = (n: number): string => {
  if (n >= 1_000_000) {
    return `${Number((n / 1_000_000).toFixed(1))}M`;
  }
  return n >= 1000 ? `${Number((n / 1000).toFixed(1))}k` : `${n}`;
};

const formatCost = (usd: number): string => {
  if (usd >= 1) {
    return `$${usd.toFixed(2)}`;
  }
  return usd >= 0.01 ? `$${usd.toFixed(3)}` : `$${usd.toFixed(4)}`;
};

export const thoughtLabel = (thought: Pick<ThoughtProcess, 'durationMs'>): string =>
  thought.durationMs > 0
    ? msg('vega.conversation.thoughtFor', { duration: formatDuration(thought.durationMs) })
    : msg('vega.conversation.reasoning');

export const thoughtSummaryParts = (
  thought: Pick<ThoughtProcess, 'toolCount' | 'reasoningCount' | 'errorCount'>,
): Array<{ key: string; text: string; isError?: boolean }> =>
  [
    thought.errorCount > 0
      ? {
          key: 'errors',
          text: msg('observability.conversationTranscript.errorCount', { count: thought.errorCount }),
          isError: true,
        }
      : undefined,
    thought.reasoningCount > 0
      ? {
          key: 'messages',
          text: msg('vega.conversation.reasoningMessages', { count: thought.reasoningCount }),
        }
      : undefined,
    thought.toolCount > 0
      ? { key: 'calls', text: msg('vega.conversation.toolCalls', { count: thought.toolCount }) }
      : undefined,
  ].filter((part) => part !== undefined);

const ThoughtSummary: React.FC<{ thought: Pick<ThoughtProcess, 'toolCount' | 'reasoningCount' | 'errorCount'> }> = ({
  thought,
}) => (
  <>
    {thoughtSummaryParts(thought).map((part, index) => (
      <Fragment key={part.key}>
        {index > 0 ? ' · ' : null}
        {part.isError ? <ErrorChip count={thought.errorCount} /> : <span>{part.text}</span>}
      </Fragment>
    ))}
  </>
);

export const thoughtUsage = (
  thought: Pick<ThoughtProcess, 'tokensIn' | 'tokensOut' | 'cacheRead' | 'costUsd'>,
): string => {
  const parts: string[] = [];
  if (thought.tokensIn > 0) {
    parts.push(msg('vega.conversation.tokensIn', { tokens: formatTokens(thought.tokensIn) }));
  }
  if (thought.tokensOut > 0) {
    parts.push(msg('vega.conversation.tokensOut', { tokens: formatTokens(thought.tokensOut) }));
  }
  if (thought.cacheRead > 0) {
    parts.push(msg('vega.conversation.tokensCache', { tokens: formatTokens(thought.cacheRead) }));
  }
  if (thought.costUsd > 0) {
    parts.push(formatCost(thought.costUsd));
  }
  return parts.join(' · ');
};

export const ThoughtProcessSection = <S extends ActivityStep>({
  thought,
  isLoading = false,
  expanded,
  onToggle,
  children,
}: {
  thought: ThoughtProcess<S>;
  isLoading?: boolean;

  expanded?: boolean;
  onToggle?: () => void;
  children: ReactNode;
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = expanded ?? internalOpen;

  const hasContent = thought.items.length > 0;

  return (
    <>
      <InvestigationRow
        flat
        className={thoughtRow}
        icon={
          isLoading ? <ProgressBar aria-label={msg('vega.status.loading')} isIndeterminate size="small" /> : undefined
        }
        label={hasContent || isLoading ? thoughtLabel(thought) : undefined}
        detail={hasContent ? <ThoughtSummary thought={thought} /> : undefined}
        isLoading={isLoading}
        caret
        expanded={open}
        onToggle={
          hasContent
            ? (onToggle ??
              (() => {
                setInternalOpen((value) => !value);
              }))
            : undefined
        }
      />
      {hasContent && open ? children : null}
    </>
  );
};

const shortModel = (model: string): string => {
  const slash = model.lastIndexOf('/');
  if (slash >= 0 && slash < model.length - 1) {
    return model.slice(slash + 1);
  }
  const family = model.match(/((?:claude|gpt|o\d|gemini|llama|mistral|sonnet|opus|haiku)[\w.-]*)$/i);
  if (family?.[1]) {
    return family[1];
  }
  const parts = model.split('.');
  return parts.length >= 3 ? parts.slice(2).join('.') : model;
};

export const ThoughtUsageBar: React.FC<{
  thought: Pick<ThoughtProcess, 'tokensIn' | 'tokensOut' | 'cacheRead' | 'costUsd'>;

  model?: string;

  onViewDetails?: () => void;

  detailsLabel: string;

  onViewTraceDetails?: () => void;
}> = ({ thought, model, onViewDetails, detailsLabel, onViewTraceDetails }) => {
  const parts = [model ? shortModel(model) : undefined, thoughtUsage(thought)].filter(Boolean);

  return (
    <div className={bar}>
      {parts.length > 0 ? <span className={usage}>{parts.join(' · ')}</span> : null}
      {onViewTraceDetails ? <SpanDetailsIconButton onPress={onViewTraceDetails} /> : null}
      {onViewDetails ? (
        <button type="button" className={detailsLink} onClick={onViewDetails}>
          {detailsLabel}
        </button>
      ) : null}
    </div>
  );
};
